import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Handshake, Users, Home as HomeIcon,
  Sparkles, Plus, LogOut, Loader2, Check,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/lib/site";
import Logo from "@/components/Logo";
import Splash from "@/components/Splash";
import Field from "@/components/admin/Field";
import AiDraftPanel from "@/components/admin/AiDraftPanel";
import ForcePasswordChange from "@/components/admin/ForcePasswordChange";
import { recordContext, draftRecord } from "@/lib/aiStudio";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const MAIN_FIELDS = [
  { key: "hero_eyebrow", label: "Homepage eyebrow line", type: "loc" },
  { key: "hero_tagline", label: "Homepage tagline", type: "locarea" },
  { key: "about_intro", label: "Who we are", type: "locarea" },
  { key: "mission", label: "Mission", type: "locarea" },
  { key: "vision", label: "Vision", type: "locarea" },
  { key: "chairman_quote", label: "Chairman's words", type: "locarea" },
  { key: "cta_title", label: "Closing call-to-action title", type: "loc" },
  { key: "cta_body", label: "Closing call-to-action text", type: "locarea" },
];

const SECTIONS = {
  main: { label: "Main page", icon: HomeIcon, blurb: "Headline, who we are, mission, chairman's words" },
  sectors: {
    label: "Sectors", icon: Building2, blurb: "What the group invests in",
    collection: "sectors", display: (it) => it.name?.en || "Untitled sector",
    fields: [
      { key: "name", label: "Sector name", type: "loc" },
      { key: "description", label: "Description", type: "locarea" },
      { key: "image", label: "Photo", type: "image" },
      { key: "capabilities", label: "What we do in it", type: "loclist" },
    ],
    blank: { name: { en: "", ar: "" }, description: { en: "", ar: "" }, image: "", capabilities: [], metrics: [], related_ventures: [], gallery: [], order: 99 },
  },
  ventures: {
    label: "Joint Ventures", icon: Handshake, blurb: "Partnerships and the companies behind them",
    collection: "ventures", display: (it) => it.name || "Untitled venture",
    fields: [
      { key: "name", label: "Venture name", type: "text" },
      { key: "partner", label: "Partner company", type: "text" },
      { key: "date", label: "Launch date (e.g. May 2026)", type: "text" },
      { key: "focus", label: "Focus", type: "locarea" },
      { key: "description", label: "Description", type: "locarea" },
      { key: "image", label: "Photo or partner logo", type: "image" },
    ],
    blank: { name: "", date: "", partner: "", partner_origin: "", partner_code: "", region: "", badge: { en: "", ar: "" }, focus: { en: "", ar: "" }, description: { en: "", ar: "" }, activities: [], partner_network: [], image: "", order: 99 },
  },
  leaders: {
    label: "Leadership", icon: Users, blurb: "Board members and advisers",
    collection: "leaders", display: (it) => it.name || "Untitled person",
    fields: [
      { key: "name", label: "Full name", type: "text" },
      { key: "title", label: "Title", type: "loc" },
      { key: "bio", label: "Biography", type: "locarea" },
      { key: "photo_url", label: "Photo", type: "image" },
    ],
    blank: { name: "", title: { en: "", ar: "" }, bio: { en: "", ar: "" }, photo_url: "", linkedin: "", group: "board", order: 99 },
  },
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5">
      <form onSubmit={submit} data-testid="studio-login-form" className="w-full max-w-sm">
        <div className="flex items-center gap-3 text-primary">
          <Logo markSize={36} siteName="Al Qalaa" />
          <div>
            <p className="font-head text-2xl">Content Studio</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Al Qalaa</p>
          </div>
        </div>
        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input className="mt-1.5 h-12" type="email" required value={email} data-testid="studio-email-input" onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Password</Label>
            <Input className="mt-1.5 h-12" type="password" required value={password} data-testid="studio-password-input" onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" disabled={busy} data-testid="studio-login-btn" className="btn-gold h-12 w-full disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MainPageEditor() {
  const site = useSite();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (site?.settings && form === null) setForm(JSON.parse(JSON.stringify(site.settings)));
  }, [site?.settings, form]);

  if (!form) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(MAIN_FIELDS.map((f) => [f.key, form[f.key]]));
      await api.put("/admin/settings", payload);
      await site.refresh();
      toast.success("Saved — live on the website");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="studio-main-editor">
      {MAIN_FIELDS.map((f) => (
        <Field
          key={f.key}
          field={f}
          value={form[f.key]}
          context={recordContext(form, MAIN_FIELDS, f.key)}
          onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))}
        />
      ))}
      <button onClick={save} disabled={busy} data-testid="studio-main-save" className="btn-gold h-12 w-full disabled:opacity-60">
        {busy ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

function CollectionEditor({ section }) {
  const [items, setItems] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/${section.collection}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, [section.collection]);

  if (items === null) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  const patch = (idx, part) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...part } : it)));

  const save = async (idx) => {
    setBusy(true);
    try {
      await api.put(`/admin/${section.collection}/${items[idx].id}`, items[idx]);
      toast.success("Saved — live on the website");
      setOpenIdx(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    try {
      const { data } = await api.post(`/admin/${section.collection}`, section.blank);
      setItems((prev) => [...prev, data]);
      setOpenIdx(items.length);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (openIdx !== null && items[openIdx]) {
    const item = items[openIdx];
    return (
      <div className="space-y-5" data-testid="studio-item-editor">
        <button onClick={() => setOpenIdx(null)} data-testid="studio-item-back" className="flex items-center gap-2 text-sm text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to {section.label}
        </button>
        <AiDraftPanel
          kind={section.collection}
          fields={section.fields}
          item={item}
          testKey={`studio-${openIdx}`}
          onApply={(p) => patch(openIdx, p)}
        />
        {section.fields.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={item[f.key]}
            context={recordContext(item, section.fields, f.key)}
            onChange={(v) => patch(openIdx, { [f.key]: v })}
          />
        ))}
        <button onClick={() => save(openIdx)} disabled={busy} data-testid="studio-item-save" className="btn-gold h-12 w-full disabled:opacity-60">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="studio-collection-list">
      <button onClick={add} data-testid="studio-add-btn" className="btn-gold mb-5 h-12 w-full">
        <Plus className="me-2 inline h-4 w-4" /> Add a new {section.label.replace(/s$/, "").toLowerCase()}
      </button>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={it.id}>
            <button
              onClick={() => setOpenIdx(i)}
              data-testid={`studio-item-${i}`}
              className="w-full border border-border bg-card/40 p-5 text-start active:border-primary"
              style={{ borderRadius: "var(--radius)" }}
            >
              <p className="font-head text-xl leading-tight">{section.display(it)}</p>
              <p className="mt-1 text-xs text-muted-foreground">Tap to edit</p>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const GUIDED_TARGETS = [
  { id: "sectors", label: "A new sector", hint: "e.g. water desalination and treatment in the UAE, two plants under study" },
  { id: "ventures", label: "A new joint venture", hint: "e.g. JV signed March 2026 with a Spanish rail contractor in Abu Dhabi, metro depot construction" },
  { id: "leaders", label: "A new board member", hint: "e.g. 20 years in Gulf project finance, previously at a sovereign fund, chairs the audit committee" },
];

function GuidedFlow({ onFinish }) {
  const [target, setTarget] = useState(null);
  const [brief, setBrief] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const section = target ? SECTIONS[target] : null;

  const write = async () => {
    if (!brief.trim()) {
      toast.error("Tell me a little about it first.");
      return;
    }
    setBusy(true);
    try {
      const fields = section.fields.filter((f) => ["text", "loc", "locarea"].includes(f.type));
      const result = await draftRecord({
        kind: target,
        brief,
        fields: fields.map((f) => ({ key: f.key, label: f.label, type: f.type })),
      });
      setDraft(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/${section.collection}`, { ...section.blank, ...draft });
      toast.success("Added — live on the website");
      onFinish();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!target) {
    return (
      <div data-testid="studio-guided-step-1">
        <p className="font-head text-2xl leading-tight">What would you like to add today?</p>
        <div className="mt-6 space-y-3">
          {GUIDED_TARGETS.map((g) => (
            <button
              key={g.id}
              onClick={() => setTarget(g.id)}
              data-testid={`studio-guided-target-${g.id}`}
              className="w-full border border-border bg-card/40 p-5 text-start active:border-primary"
              style={{ borderRadius: "var(--radius)" }}
            >
              <p className="font-head text-xl">{g.label}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (!draft) {
    const hint = GUIDED_TARGETS.find((g) => g.id === target)?.hint;
    return (
      <div data-testid="studio-guided-step-2">
        <button onClick={() => setTarget(null)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="studio-guided-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <p className="mt-5 font-head text-2xl leading-tight">Tell me about it in your own words</p>
        <p className="mt-2 text-sm text-muted-foreground">
          A sentence or two is enough. I will write the English and the Arabic in the group's tone.
        </p>
        <Textarea
          rows={5}
          className="mt-4"
          value={brief}
          placeholder={hint}
          data-testid="studio-guided-brief"
          onChange={(e) => setBrief(e.target.value)}
        />
        <button onClick={write} disabled={busy} data-testid="studio-guided-write" className="btn-gold mt-5 h-12 w-full disabled:opacity-60">
          {busy ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Writing…</span>
          ) : (
            <><Sparkles className="me-2 inline h-4 w-4" /> Write it for me</>
          )}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="studio-guided-step-3">
      <p className="font-head text-2xl leading-tight">Here is the draft</p>
      <p className="mt-2 text-sm text-muted-foreground">Read it over. Nothing is published until you tap Add it.</p>
      <div className="mt-5 space-y-4">
        {section.fields
          .filter((f) => draft[f.key])
          .map((f) => {
            const v = draft[f.key];
            const en = typeof v === "object" ? v.en : v;
            const ar = typeof v === "object" ? v.ar : null;
            return (
              <div key={f.key} className="border border-border bg-card/40 p-4" style={{ borderRadius: "var(--radius)" }}>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">{f.label}</p>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed">{en}</p>
                {ar ? <p dir="rtl" className="mt-3 whitespace-pre-line border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground">{ar}</p> : null}
              </div>
            );
          })}
      </div>
      <div className="mt-6 space-y-3">
        <button onClick={publish} disabled={busy} data-testid="studio-guided-publish" className="btn-gold h-12 w-full disabled:opacity-60">
          {busy ? "Adding…" : <><Check className="me-2 inline h-4 w-4" /> Add it to the website</>}
        </button>
        <button onClick={() => setDraft(null)} disabled={busy} data-testid="studio-guided-retry" className="btn-outline h-12 w-full text-xs">
          Change the brief and rewrite
        </button>
      </div>
    </div>
  );
}

export default function Studio() {
  const [user, setUser] = useState(undefined);
  const [view, setView] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setView(null);
  };

  const title = useMemo(() => {
    if (view === "guided") return "Guided writing";
    return view ? SECTIONS[view]?.label : "Content Studio";
  }, [view]);

  if (user === undefined) return <Splash />;
  if (!user) return <LoginScreen onLogin={setUser} />;
  if (user.must_change_password) return <ForcePasswordChange user={user} onDone={setUser} />;

  return (
    <div className="min-h-screen bg-background" data-testid="studio-page">
      <header className="glass-nav sticky top-0 z-40 border-b border-border">
        <div className="mx-auto flex h-16 max-w-xl items-center justify-between px-4">
          <div className="flex min-w-0 items-center gap-2">
            {view ? (
              <button onClick={() => setView(null)} data-testid="studio-back-btn" className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary">
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : (
              <Logo markSize={26} siteName="Al Qalaa" />
            )}
            <p className="truncate font-head text-lg">{title}</p>
          </div>
          <div className="flex items-center gap-2">
            {user.role === "admin" && !view && (
              <Link to="/admin" data-testid="studio-admin-link" className="text-xs text-muted-foreground hover:text-primary">
                Full panel
              </Link>
            )}
            <button onClick={logout} data-testid="studio-logout-btn" className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-7">
        {!view && (
          <>
            <p className="font-head text-3xl leading-tight">Hello{user.name ? `, ${user.name.split(" ")[0]}` : ""}.</p>
            <p className="mt-2 text-sm text-muted-foreground">What are we updating today?</p>

            <button
              onClick={() => setView("guided")}
              data-testid="studio-guided-btn"
              className="mt-6 flex w-full items-center gap-4 border border-primary/50 bg-primary/5 p-5 text-start active:bg-primary/10"
              style={{ borderRadius: "var(--radius)" }}
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Sparkles className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-head text-xl">Let AI write it</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">Describe it, I draft the English and Arabic</span>
              </span>
            </button>

            <div className="mt-5 space-y-3">
              {Object.entries(SECTIONS).map(([id, s]) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  data-testid={`studio-section-${id}`}
                  className="flex w-full items-center gap-4 border border-border bg-card/40 p-5 text-start active:border-primary"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-primary">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-head text-xl">{s.label}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{s.blurb}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {view === "guided" && <GuidedFlow onFinish={() => setView(null)} />}
        {view === "main" && <MainPageEditor />}
        {view && SECTIONS[view]?.collection && <CollectionEditor section={SECTIONS[view]} />}
      </main>
    </div>
  );
}
